import type {
  DensitySnapshot,
  DensityStateView,
  InstrumentRef,
  MarketKind,
} from "@screener/protocol";
import { activeDisplayMinAgeSec, type DisplayMinAgeState } from "./display-min-age";
import {
  buildMinimumsIndex,
  type DisplayMinimumsState,
  effectiveMinimum,
  type MinimumsIndex,
} from "./display-minimums";
import type { FavoritesState } from "./favorite-match";
import { isFavoriteInstrument } from "./favorite-match";

/**
 * Границы шкалы полосы объёма, USD.
 *
 * Это единственные числовые литералы экрана, и они здесь намеренно:
 * §9 (1.9) называет границы частью описания экрана, а не настройкой §11.
 * Шкала обязана значить одно и то же всегда — при любом составе строк,
 * на любой бирже, до и после фильтра. Нормировка на максимум видимых строк
 * запрещена там же: при ней одна и та же стена меняет длину, когда рядом
 * появляется или уходит чужая.
 */
export const BAR_SCALE_MIN_USD = 100_000;
export const BAR_SCALE_MAX_USD = 10_000_000;

const LOG_MIN = Math.log10(BAR_SCALE_MIN_USD);
const LOG_MAX = Math.log10(BAR_SCALE_MAX_USD);

/**
 * Режим экрана §9 — четыре представления одного живого состава (§9 1.21):
 * карточки (основной вид), по монете, таблица плотностей и список ситуаций
 * §5. Живёт здесь, а не в `density-screen.tsx`, ровно как
 * `DensitySort`/`DensityFilters` — общий тип для экрана и панели фильтров
 * без циклического импорта между ними.
 */
export type ScreenMode = "cards" | "coins" | "densities" | "situations";

/** Ключ сортировки §9: объём в USD, время жизни, дистанция — и ничего иного. */
export type DensitySortKey = "usd" | "age" | "distance";

export interface DensitySort {
  key: DensitySortKey;
  direction: "asc" | "desc";
}

/**
 * Направление сортировки по умолчанию для ключа §9: по дистанции —
 * по возрастанию (ближайшая плотность первая), по остальным ключам — по
 * убыванию. Используется и переключателем режима сортировки (первый клик по
 * ключу), и выбором представителя карточки (§9 1.21, design review, блок B)
 * — оба места не вправе разойтись в этом выборе.
 */
export function defaultSortDirection(key: DensitySortKey): "asc" | "desc" {
  return key === "distance" ? "asc" : "desc";
}

/**
 * Ключ рынка биржи в отборе (§115 п. 1): «<биржа>:<рынок>». Рынок берётся из
 * `instrument.market` контракта, своего перечня рынков клиент не держит.
 */
export function marketKey(instrument: Pick<InstrumentRef, "exchange" | "market">): string {
  return `${instrument.exchange}:${instrument.market}`;
}

/** Биржи среди ключей рынков потока, в порядке первого появления. */
export function exchangesOf(known: readonly string[]): string[] {
  return [...new Set(known.map((key) => key.slice(0, key.lastIndexOf(":"))))];
}

/**
 * Место рынка в строке биржи: сначала спот, потом бессрочные, у всех бирж
 * одинаково. `Record` по `MarketKind`: новый вид рынка в контракте ломает сборку.
 */
const MARKET_RANK: Record<MarketKind, number> = {
  spot: 0,
  perp: 1,
};

export const MARKET_ORDER: readonly MarketKind[] = (Object.keys(MARKET_RANK) as MarketKind[]).sort(
  (left, right) => MARKET_RANK[left] - MARKET_RANK[right],
);

/** Рынки одной биржи среди ключей рынков потока, в порядке `MARKET_ORDER` (spot, perp). */
export function marketsOf(known: readonly string[], exchange: string): MarketKind[] {
  return MARKET_ORDER.filter((market) => known.includes(`${exchange}:${market}`));
}

/**
 * Нормализация отбора бирж (§70 п. 4, §115 п. 5): если включён каждый рынок
 * потока, отбора нет — `null`. Пустой перечень рынков (поток ещё не прислал
 * их) не нормализуется, иначе сохранённый отбор схлопнулся бы до прихода потока.
 * Лишний ключ рынка, которого в потоке нет, нормализации не мешает.
 */
export function normalizeExchangeSelection(selected: string[], known: string[]): string[] | null {
  if (known.length > 0 && known.every((key) => selected.includes(key))) {
    return null;
  }
  return selected;
}

/** Переключает один рынок биржи: `null` («без отбора») читается как «включены все». */
export function toggleMarket(
  selected: string[] | null,
  key: string,
  known: string[],
): string[] | null {
  const current = selected ?? known;
  const next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key];
  return normalizeExchangeSelection(next, known);
}

/**
 * Переключает все рынки биржи (§115 п. 2): включён хотя бы один — выключает
 * все, не включён ни один — включает все.
 */
export function toggleExchange(
  selected: string[] | null,
  exchange: string,
  known: string[],
): string[] | null {
  const current = selected ?? known;
  const own = marketsOf(known, exchange).map((market) => `${exchange}:${market}`);
  const anyOn = own.some((key) => current.includes(key));
  const next = anyOn
    ? current.filter((key) => !own.includes(key))
    : [...current, ...own.filter((key) => !current.includes(key))];
  return normalizeExchangeSelection(next, known);
}

/**
 * Действует ли отбор по биржам: он задан и не равен «все известные биржи».
 * По нему решается, показывать ли «Снять отбор» и называть ли биржи в сводке.
 */
export function exchangeFilterActive(filters: DensityFilters, known: string[]): boolean {
  return (
    filters.exchanges !== null && normalizeExchangeSelection(filters.exchanges, known) !== null
  );
}

export interface DensityFilters {
  /** Минимальный размер плотности в USD; `null` — фильтр не применён. */
  minUsd: number | null;
  /** Пустой список — стадии не фильтруются. */
  states: DensityStateView[];
  /**
   * Включённые рынки бирж — ключи «<биржа>:<рынок>» (§115), либо `null` —
   * фильтр не применён.
   *
   * Пустой список и «фильтр не применён» здесь разные вещи намеренно: если бы
   * пустота значила «показывать всё», человек, выключающий кнопки одну за
   * другой, на последней получал бы обратное тому, что нажал — все строки
   * вернулись бы разом.
   */
  exchanges: string[] | null;
  hideMm: boolean;
  /**
   * Оставляет только инструменты, относящиеся к записи избранного §6.6
   * (спека `user-favorites`, `web-density-table`). Избранное — данные
   * экрана, а не его настройка: непрочитанный список делает отбор честно
   * пустым с отдельной причиной, а не «фильтр снят».
   */
  onlyFavorites: boolean;
}

export const defaultFilters: DensityFilters = {
  minUsd: null,
  states: [],
  exchanges: null,
  hideMm: false,
  onlyFavorites: false,
};

/** Ключи фильтров экрана, каждый из которых снимается по отдельности. */
export type FilterKey = keyof DensityFilters;

/** Сколько фильтров экрана задано: число на кнопке «Фильтры». */
export function activeFilterCount(filters: DensityFilters, exchanges: string[]): number {
  return [
    filters.minUsd !== null,
    filters.states.length > 0,
    exchangeFilterActive(filters, exchanges),
    filters.hideMm,
    filters.onlyFavorites,
  ].filter(Boolean).length;
}

/** Фильтры, где только `key` возвращён к умолчанию. */
export function clearFilter<K extends FilterKey>(filters: DensityFilters, key: K): DensityFilters {
  return { ...filters, [key]: defaultFilters[key] };
}

/**
 * Доля шкалы, которую занимает полоса объёма: 0 у нижней границы, 1 у верхней.
 * Доля — та самая статистика, для которой правила разрешают число
 * с плавающей точкой. Сам объём остаётся строкой контракта и в число
 * для показа не превращается: `Decimal` потерял бы на этом точность.
 */
export function volumeBarFraction(densityUsd: string): number {
  const value = Number(densityUsd);
  if (!Number.isFinite(value) || value <= 0 || densityUsd.trim() === "") {
    return 0;
  }
  const fraction = (Math.log10(value) - LOG_MIN) / (LOG_MAX - LOG_MIN);
  return Math.min(1, Math.max(0, fraction));
}

/**
 * Сравнение по дистанции. Плотность без известной дистанции уходит в конец
 * при любом направлении: ноль по §2 означает касание, то есть самое тревожное
 * значение шкалы, и пустое значение занимать его место не вправе.
 */
function compareDistance(left: DensitySnapshot, right: DensitySnapshot, sign: number): number {
  const leftKnown = left.distance_pct !== null;
  const rightKnown = right.distance_pct !== null;
  if (!leftKnown && !rightKnown) {
    return 0;
  }
  if (!leftKnown) {
    return 1;
  }
  if (!rightKnown) {
    return -1;
  }
  return sign * ((left.distance_pct ?? 0) - (right.distance_pct ?? 0));
}

export function sortRows(rows: DensitySnapshot[], sort: DensitySort): DensitySnapshot[] {
  const sign = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    if (sort.key === "usd") {
      return sign * (Number(left.density_usd) - Number(right.density_usd));
    }
    if (sort.key === "age") {
      // Возраст растёт вниз по времени обнаружения: младшая плотность
      // обнаружена позже, поэтому по возрастанию возраста порядок обратный.
      return sign * (right.detected_at_ms - left.detected_at_ms);
    }
    return compareDistance(left, right, sign);
  });
}

/**
 * Отбор видимых строк. Ни одна величина строки при этом не меняется — в том
 * числе длина полосы объёма: возвращаются те же объекты, что пришли.
 *
 * `favorites` — состояние списка избранного экрана, а не настройка отбора:
 * когда `onlyFavorites` включён, а список ещё не прочитан или недоступен,
 * отбор возвращает пустой состав, а не «показать всё» — иначе ошибка
 * хранилища читалась бы как «избранных плотностей нет» (спека
 * `web-density-table` «Избранное не прочитано»). Причина этой пустоты
 * называется отдельно, `favoritesFilterUnavailable`.
 *
 * `favorites` — обязательный параметр, не аргумент по умолчанию (review
 * stage6-favorites, п. 6): состояние избранного участвует в отборе всегда,
 * и вызов, забывший его передать, обязан не собраться, а не тихо считать
 * фильтр недоступным.
 *
 * `minimums` — личные минимумы объёма §59, тем же доводом обязательный
 * четвёртый параметр: действует минимум монеты на её бирже и рынке, иначе
 * минимум рынка, поверх фильтров §9 и той же величиной, что `minUsd`.
 */
/**
 * Плотность ещё не подтверждена: `Detected` — промежуточная стадия до `Pending`
 * (демон шлёт оба перехода подряд), и на экране она ведёт себя как `Pending`.
 * Иначе граница кадра между сообщениями выпускает строку на экран на один кадр.
 */
export function isUnconfirmed(state: DensityStateView): boolean {
  return state === "detected" || state === "pending";
}

export function filterRows(
  rows: readonly DensitySnapshot[],
  filters: DensityFilters,
  favorites: FavoritesState,
  minimums: DisplayMinimumsState,
  minAge: DisplayMinAgeState,
  nowMs: number,
): DensitySnapshot[] {
  // `Pending` не прожил `min_lifetime_sec` и по умолчанию скрыт (§9);
  // включается только фишкой стадии (§69).
  const showPending = filters.states.includes("pending");
  // Время до показа §82 действует вместо подтверждения: пока оно не задано,
  // прочитано с ошибкой или ещё грузится, отбор ровно прежний.
  const minAgeSec = activeDisplayMinAgeSec(minAge);

  if (filters.onlyFavorites && favorites.status !== "ready") {
    return [];
  }

  // Индекс личных минимумов §59 — один раз на вызов. Пока список не
  // прочитан, личный минимум строк не отбирает (design.md
  // stage8-personal-display-filters, Д5): ошибка хранилища не прячет данные.
  const minimumsIndex: MinimumsIndex | null =
    minimums.status === "ready" ? buildMinimumsIndex(minimums.minimums) : null;

  return rows.filter((row) => {
    if (minAgeSec !== null && ageSeconds(row, nowMs) < minAgeSec) {
      return false;
    }
    if (minAgeSec === null && !showPending && isUnconfirmed(row.state)) {
      return false;
    }
    if (filters.minUsd !== null && Number(row.density_usd) < filters.minUsd) {
      return false;
    }
    if (minimumsIndex !== null) {
      const personalMin = effectiveMinimum(row.instrument, minimumsIndex);
      if (personalMin !== null && Number(row.density_usd) < personalMin) {
        return false;
      }
    }
    // `Detected` отбирается фишкой `Pending`: своей фишки у него нет (Д10).
    const stageState: DensityStateView = isUnconfirmed(row.state) ? "pending" : row.state;
    if (filters.states.length > 0 && !filters.states.includes(stageState)) {
      return false;
    }
    if (filters.exchanges !== null && !filters.exchanges.includes(marketKey(row.instrument))) {
      return false;
    }
    if (filters.hideMm && row.mm_flags.length > 0) {
      return false;
    }
    if (
      filters.onlyFavorites &&
      favorites.status === "ready" &&
      !isFavoriteInstrument(row.instrument, favorites.favorites)
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Причина, по которой «только избранные» не может отобрать ничего честно:
 * фильтр включён, а список избранного ещё не прочитан или недоступен. Экран
 * называет её отдельными словами вместо «плотностей нет» или «отбор ничего
 * не пропустил» (спека `web-density-table`/`web-situation-list`).
 */
export function favoritesFilterUnavailable(
  filters: DensityFilters,
  favorites: FavoritesState,
): boolean {
  return filters.onlyFavorites && favorites.status !== "ready";
}

/**
 * Секунды от момента `ms` до общего «сейчас» таблицы. Общий низкоуровневый
 * расчёт возраста — им пользуется и `ageSeconds` для строки плотности, и
 * список ситуаций для минимума/максимума времени жизни группы (`situation-
 * row.tsx`), чтобы формула не повторялась посимвольно в двух местах.
 */
export function secondsSince(ms: number, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - ms) / 1000));
}

/** Время жизни плотности в секундах от общего «сейчас» таблицы (§9). */
export function ageSeconds(row: DensitySnapshot, nowMs: number): number {
  return secondsSince(row.detected_at_ms, nowMs);
}

/** Параметры отличения трёх пустых состояний вида плотностей (таблица и карточки). */
export interface DensityEmptyMessageParams {
  /** Приветствие ещё не пришло: вида нет вовсе, а не он пуст. */
  awaitingHello: boolean;
  /** Сколько строк было до фильтров — чтобы отличить «не пропустили» от «нет данных». */
  totalRows: number;
  /** «Только избранные» включён, а список избранного ещё не прочитан или недоступен. */
  favoritesUnavailable: boolean;
}

/**
 * Текст пустого состояния вида плотностей §9 — общий у таблицы и карточек
 * (design.md change stage6-density-cards, задача 6.1): оба вида различают
 * одни и те же три причины пустоты одними и теми же словами, и вынос сюда
 * не даёт им разойтись при следующей правке текста.
 */
export function densityEmptyMessage({
  awaitingHello,
  totalRows,
  favoritesUnavailable,
}: DensityEmptyMessageParams): string {
  if (awaitingHello) {
    return "Данных ещё нет: приветствие демона не пришло.";
  }
  if (totalRows === 0) {
    return "Живых плотностей нет — демон не прислал ни одной.";
  }
  if (favoritesUnavailable) {
    return "Избранное недоступно: список не прочитан.";
  }
  return "Фильтры не пропустили ни одной строки.";
}

/**
 * Число колонок таблицы §9 — общее у `DensityTable` и `SituationList`: состав
 * колонок один и тот же, и `colSpan` пустого состояния обязан считаться от
 * одной константы, а не от двух литералов, которые молча разойдутся при
 * следующей колонке.
 */
export const DENSITY_TABLE_COLUMN_COUNT = 13;
